import React, { useState, useEffect, useMemo } from "react";
import { PacienteApi } from "../../api/Paciente";
import { TurnosApi } from "../../api/Turnos";
import { EstudiosApi } from "../../api/Estudios";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import "./ModalAsignarTurno.css";

const pacienteApi = new PacienteApi();
const turnosApi = new TurnosApi();
const estudiosApi = new EstudiosApi();

export default function ModalAsignarTurno({ turno, onClose, onAsignado }) {
    const [pacientes, setPacientes] = useState([]);
    const [estudios, setEstudios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingPacientes, setLoadingPacientes] = useState(true);
    const [loadingEstudios, setLoadingEstudios] = useState(true);
    const [busqueda, setBusqueda] = useState("");
    const [busquedaEstudio, setBusquedaEstudio] = useState("");
    const [esInvitado, setEsInvitado] = useState(false);
    const [formData, setFormData] = useState({
        pacienteId: "",
        estudioId: "",
        motivoConsulta: "",
        telefonoRegistrado: "",
        // Campos para invitado
        nombre: "",
        dni: "",
        telefono: "",
    });

    useEffect(() => {
        cargarPacientes();
        cargarEstudios();
    }, []);

    const cargarPacientes = async () => {
        setLoadingPacientes(true);
        try {
            let response = await pacienteApi.getPacientesShort();

            if (!response || (Array.isArray(response) && response.length === 0) ||
                (response.pacientes && response.pacientes.length === 0)) {
                response = await pacienteApi.getPacientes();
            }

            let listaPacientes = [];
            if (Array.isArray(response)) {
                listaPacientes = response;
            } else if (response && response.pacientes && Array.isArray(response.pacientes)) {
                listaPacientes = response.pacientes;
            }

            if (listaPacientes.length > 0) {
                const pacientesOrdenados = listaPacientes.sort((a, b) => {
                    const nombreA = a.nombre?.toLowerCase() || "";
                    const nombreB = b.nombre?.toLowerCase() || "";
                    return nombreA.localeCompare(nombreB);
                });
                setPacientes(pacientesOrdenados);
            } else {
                toast.error("No hay pacientes registrados en el sistema");
                setPacientes([]);
            }
        } catch (error) {
            console.error("Error al cargar pacientes:", error);
            toast.error("Error al cargar la lista de pacientes");
            setPacientes([]);
        } finally {
            setLoadingPacientes(false);
        }
    };

    const cargarEstudios = async () => {
        setLoadingEstudios(true);
        try {
            const response = await estudiosApi.getEstudios(true);
            const listaEstudios = Array.isArray(response)
                ? response
                : response.estudios || [];
            setEstudios(listaEstudios.filter(e => e.activo));
        } catch (error) {
            console.error("Error al cargar estudios:", error);
            toast.error("Error al cargar los estudios disponibles");
            setEstudios([]);
        } finally {
            setLoadingEstudios(false);
        }
    };

    const pacientesFiltrados = useMemo(() => {
        if (!busqueda.trim()) {
            return pacientes;
        }
        const busquedaLower = busqueda.toLowerCase();
        return pacientes.filter((paciente) => {
            const nombre = paciente.nombre?.toLowerCase() || "";
            const dni = paciente.dni?.toString() || "";
            const email = paciente.email?.toLowerCase() || "";
            return (
                nombre.includes(busquedaLower) ||
                dni.includes(busquedaLower) ||
                email.includes(busquedaLower)
            );
        });
    }, [pacientes, busqueda]);

    const estudiosFiltrados = useMemo(() => {
        if (!busquedaEstudio.trim()) {
            return estudios;
        }
        const busquedaLower = busquedaEstudio.toLowerCase();
        return estudios.filter((estudio) => {
            const tipo = estudio.tipo?.toLowerCase() || "";
            return tipo.includes(busquedaLower);
        });
    }, [estudios, busquedaEstudio]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const newFormData = { ...formData, [name]: value };
        // Auto-fill telefono when selecting a registered patient
        if (name === "pacienteId" && !esInvitado) {
            const pac = pacientes.find((p) => p._id === value);
            if (pac && pac.telefono) {
                newFormData.telefonoRegistrado = pac.telefono;
            } else {
                newFormData.telefonoRegistrado = "";
            }
        }
        setFormData(newFormData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!esInvitado && !formData.pacienteId) {
            toast.error("Debe seleccionar un paciente");
            return;
        }

        if (!esInvitado && !formData.telefonoRegistrado.trim()) {
            toast.error("Debe ingresar el teléfono del paciente");
            return;
        }

        if (esInvitado && (!formData.nombre || !formData.dni || !formData.telefono)) {
            toast.error("Debe completar todos los datos del paciente");
            return;
        }

        if (!formData.estudioId) {
            toast.error("Debe seleccionar un estudio");
            return;
        }

        // Verificar si el estudio tiene aclaraciones
        if (estudioSeleccionado && estudioSeleccionado.aclaraciones) {
            const result = await Swal.fire({
                title: "⚠️ Aclaraciones del Estudio",
                html: `
                    <div style="text-align: left; padding: 10px;">
                        <p style="margin-bottom: 10px;"><strong>Estudio:</strong> ${estudioSeleccionado.tipo}</p>
                        <div style="background: #fff8e1; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107;">
                            <p style="margin: 0; color: #5d4037;"><strong>Aclaraciones importantes:</strong></p>
                            <p style="margin: 10px 0 0 0; color: #666;">${estudioSeleccionado.aclaraciones}</p>
                        </div>
                        <p style="margin-top: 15px; color: #888; font-size: 0.9rem;">¿Desea continuar con la asignación del turno?</p>
                    </div>
                `,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#28a745",
                cancelButtonColor: "#6c757d",
                confirmButtonText: "Sí, asignar turno",
                cancelButtonText: "Cancelar",
            });

            if (!result.isConfirmed) {
                return;
            }
        }

        setLoading(true);

        try {
            let response;
            if (esInvitado) {
                response = await turnosApi.reservarTurnoInvitado(turno._id, {
                    nombre: formData.nombre,
                    dni: formData.dni,
                    telefono: formData.telefono,
                    motivoConsulta: formData.motivoConsulta,
                    estudioId: formData.estudioId
                });
            } else {
                // Obtener el teléfono del paciente seleccionado
                const telPaciente = formData.telefonoRegistrado;
                response = await turnosApi.reservarTurno(
                    turno._id,
                    formData.pacienteId,
                    formData.motivoConsulta,
                    formData.estudioId,
                    telPaciente
                );
            }

            if (response && response.turno) {
                toast.success(`✓ Turno asignado exitosamente ${esInvitado ? "al invitado" : "al paciente"}`);
                onAsignado();
                onClose();
            } else {
                throw new Error("No se recibió confirmación del servidor");
            }
        } catch (error) {
            console.error("Error al asignar turno:", error);
            toast.error(error.message || "Error al asignar el turno");
        } finally {
            setLoading(false);
        }
    };

    const pacienteSeleccionado = pacientes.find((p) => p._id === formData.pacienteId);
    const estudioSeleccionado = estudios.find((e) => e._id === formData.estudioId);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content-asignar" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h4>Asignar Turno a Paciente</h4>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    <div className="turno-info-box">
                        <h5>Información del Turno</h5>
                        <p>
                            <strong>Fecha y Hora:</strong>{" "}
                            {new Date(turno.fecha).toLocaleString("es-AR", {
                                weekday: "long",
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                            })}
                        </p>
                        <p><strong>Doctor:</strong> {turno.doctor?.nombre || "No especificado"}</p>
                        <p><strong>Especialidad:</strong> {turno.especialidad || "No especificada"}</p>
                    </div>


                    <div className="registro-mode-selector">
                        <button
                            type="button"
                            className={`mode-btn ${!esInvitado ? 'active' : ''}`}
                            onClick={() => setEsInvitado(false)}
                        >
                            Paciente Registrado
                        </button>
                        <button
                            type="button"
                            className={`mode-btn ${esInvitado ? 'active' : ''}`}
                            onClick={() => setEsInvitado(true)}
                        >
                            Paciente No Registrado
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {!esInvitado ? (
                            <>
                                <div className="form-group">
                                    <label htmlFor="busqueda">Buscar Paciente</label>
                                    <input
                                        type="text"
                                        id="busqueda"
                                        placeholder="Buscar por nombre, DNI o email..."
                                        value={busqueda}
                                        onChange={(e) => setBusqueda(e.target.value)}
                                        disabled={loading || loadingPacientes}
                                        className="input-busqueda"
                                    />
                                    <small className="help-text">
                                        {loadingPacientes
                                            ? "Cargando pacientes..."
                                            : `${pacientesFiltrados.length} de ${pacientes.length} pacientes`}
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="pacienteId">
                                        Seleccionar Paciente <span className="required">*</span>
                                    </label>
                                    <select
                                        id="pacienteId"
                                        name="pacienteId"
                                        value={formData.pacienteId}
                                        onChange={handleChange}
                                        required={!esInvitado}
                                        disabled={loading || loadingPacientes}
                                        size="6"
                                        className="select-pacientes"
                                    >
                                        <option value="">-- Seleccione un paciente --</option>
                                        {pacientesFiltrados.map((paciente) => (
                                            <option key={paciente._id} value={paciente._id}>
                                                {paciente.nombre} {paciente.dni ? `- DNI: ${paciente.dni}` : ""}
                                            </option>
                                        ))}
                                    </select>
                                    {pacientesFiltrados.length === 0 && !loadingPacientes && (
                                        <small className="help-text error-text">
                                            No se encontraron pacientes con ese criterio de búsqueda
                                        </small>
                                    )}
                                </div>

                                {pacienteSeleccionado && (
                                    <>
                                    <div className="paciente-info-box">
                                        <p><strong>Email:</strong> {pacienteSeleccionado.email || "No especificado"}</p>
                                        <p><strong>Teléfono registrado:</strong> {pacienteSeleccionado.telefono || "No tiene"}</p>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="telefonoRegistrado">
                                            Teléfono de contacto <span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="telefonoRegistrado"
                                            name="telefonoRegistrado"
                                            placeholder="Teléfono del paciente..."
                                            value={formData.telefonoRegistrado}
                                            onChange={handleChange}
                                            required={!esInvitado}
                                            disabled={loading}
                                        />
                                        <small className="help-text">Requerido para confirmar el turno</small>
                                    </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="guest-form-section">
                                <div className="form-group">
                                    <label htmlFor="nombre">Nombre Completo <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        id="nombre"
                                        name="nombre"
                                        placeholder="Nombre y Apellido del paciente..."
                                        value={formData.nombre}
                                        onChange={handleChange}
                                        required={esInvitado}
                                        disabled={loading}
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="dni">DNI / Identificación <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id="dni"
                                            name="dni"
                                            placeholder="DNI del paciente..."
                                            value={formData.dni}
                                            onChange={handleChange}
                                            required={esInvitado}
                                            disabled={loading}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="telefono">Teléfono <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            id="telefono"
                                            name="telefono"
                                            placeholder="Teléfono de contacto..."
                                            value={formData.telefono}
                                            onChange={handleChange}
                                            required={esInvitado}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="busquedaEstudio">Buscar Estudio</label>
                            <input
                                type="text"
                                id="busquedaEstudio"
                                placeholder="Buscar estudio por nombre..."
                                value={busquedaEstudio}
                                onChange={(e) => setBusquedaEstudio(e.target.value)}
                                disabled={loading || loadingEstudios}
                                className="input-busqueda"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="estudioId">
                                Seleccionar Estudio <span className="required">*</span>
                            </label>
                            <select
                                id="estudioId"
                                name="estudioId"
                                value={formData.estudioId}
                                onChange={handleChange}
                                required
                                disabled={loading || loadingEstudios}
                                className="select-estudio"
                                size={estudiosFiltrados.length > 5 ? "6" : "1"}
                            >
                                <option value="">
                                    {loadingEstudios ? "Cargando estudios..." : "-- Seleccione un estudio --"}
                                </option>
                                {estudiosFiltrados.map((estudio) => (
                                    <option key={estudio._id} value={estudio._id}>
                                        {estudio.tipo} {estudio.precio ? `- $${estudio.precio}` : ""}
                                    </option>
                                ))}
                            </select>
                            {estudiosFiltrados.length === 0 && !loadingEstudios && (
                                <small className="help-text error-text">
                                    {busquedaEstudio.trim()
                                        ? "No se encontraron estudios con ese criterio"
                                        : "No hay estudios disponibles"}
                                </small>
                            )}
                        </div>

                        {estudioSeleccionado && estudioSeleccionado.aclaraciones && (
                            <div className="estudio-info-box">
                                <p><strong>Aclaraciones:</strong> {estudioSeleccionado.aclaraciones}</p>
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="motivoConsulta">
                                Motivo de Consulta <span className="required">*</span>
                            </label>
                            <textarea
                                id="motivoConsulta"
                                name="motivoConsulta"
                                value={formData.motivoConsulta}
                                onChange={handleChange}
                                placeholder="Describa el motivo de la consulta..."
                                rows="4"
                                disabled={loading}
                            />
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn-cancelar-modal"
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn-asignar-modal"
                                disabled={loading || loadingPacientes || loadingEstudios}
                            >
                                {loading ? "Asignando..." : "Asignar Turno"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
